<template>
  <div>
    <div class="mb-3 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <CInput v-if="isEditing" v-model="campaignCopy.name" />
        <div v-else class="font-weight-bold">{{ campaignCopy.name }}</div>
        <CButton
          @click="handleEdit"
          color="primary"
          class="ml-3 d-flex align-items-center"
          size="sm"
        >
          <em v-if="isEditing" class="mz-icon icon-save" />
          <em v-else class="mz-icon icon-edit" />
        </CButton>
        <div class="ml-3 badge-light px-2 py-1">
          {{campaign.language}}
        </div>
        <div class="ml-3 badge-light px-2 py-1 font-weight-bold">
          {{formTypeOptionsMap[campaign.form_id || 1]}}
        </div>
      </div>
      <CLink
        v-if="campaign.nb_alumni"
        :to="{ name: 'CampaignStats' }"
      >
        <span class="font-weight-bold">
          {{ campaign.percentage_completed }}%
        </span>
        des alumnis ont complété le formulaire
      </CLink>
    </div>
    <div class="buttons-wrapper mb-3">
      <div>
        <CButton
          class="mb-1"
          size="sm"
          style="background-color: #2271AE; color: white"
          @click="
            () =>
              downloadFile({
                fileName: 'alumni_template.xlsx',
                downloadURL: ALUMNI_ENDPOINTS.template,
              })
          "
        >
          Télécharger le template alumni
        </CButton>
      </div>
      <FileDragdrop @upload="uploadFile" data-cy="upload-alumni" ref="upload">
        <template slot="upload-button">
          <CButton
            type="file"
            size="sm"
            style="background-color: #2271AE; color: white"
            @click="() => this.uploadType = 'alumni'"
          >
            Charger le fichier d'invitation alumni
          </CButton>
        </template>
      </FileDragdrop>
      <div>
        <CButton
          size="sm"
          style="background-color: #2271AE; color: white"
          @click="exportScopeOfCampain"
          :disabled="!campaign.nb_alumni"
        >
          Télécharger le fichier invitation
        </CButton>
      </div>
      <FileDragdrop @upload="uploadFile"  data-cy="upload-alumni-invite" ref="upload">
        <template slot="upload-button">
          <CButton
            type="file"
            size="sm"
            style="background-color: #2271AE; color: white"
            @click="() => this.uploadType = 'invite'"
          >
            Mettre à jour le fichier invitation
          </CButton>
        </template>
      </FileDragdrop>
      <div>
        <CButton
          size="sm"
          style="background-color: #2291EE; color: white"
          to="mail"
          :disabled="!campaign.nb_alumni"
        >
          Modèle d'email
        </CButton>
      </div>
      <div>
        <CButton
          size="sm"
          style="background-color: #2291EE; color: white"
          @click="handleSend"
          :disabled="!campaign.nb_alumni"
        >
          Envoyer email
        </CButton>
      </div>
      <div>
        <CButton
          size="sm"
          style="background-color: #1131AE; color: white"
          @click="
            () =>
              downloadFile({
                downloadURL: CAMPAGNES_ENDPOINTS.export(campaign.id),
                query: $refs.alumniTable.tableQuery
              })
          "
          :disabled="!campaign.nb_alumni"
          data-cy="download-campaign-answers"
        >
          Télécharger les réponses de la campagne
        </CButton>
      </div>
    </div>
    <div v-if="isUploading" class="mt-3 d-flex justify-content-center align-items-center">
      <h4 class="mr-3">Loading invite file ...</h4>
      <dots-loader/>
    </div>
    <table-component
      v-if="campaign.id && !isUploading"
      :url="ALUMNI_ENDPOINTS.list(campaign.id)"
      :columns="computedColumns"
      selection-column="form_uuid"
      :hidden-columns="['form_uuid']"
      :filter-exclude-columns="['actions']"
      :key="`${campaign.nb_alumni}${refreshAlumnis}`"
      :headings="tableHeadings"
      :pagination-text-position="'bottom'"
      :display-columns-selector="false"
      ref="alumniTable"
      :should-allow-row-selection="isAdministrator"
      @row-selected="selectAlumnis"
      :force-filter="true"
      :init-filters="currentFilters"
      :on-result="setFilters"
      enableSelectAllRowsOnServerTable
    >
      <template #row-selection-button>
        <div class="d-flex flex-row gap-2">
          <div v-if="multiSelected" class="d-flex align-items-center justify-content-start gap-2">
            <CButton
                size="sm"
                color="primary"
                @click="openEditModal(selectedAlumnis, true)"
            >
              Edit selection
              <i class="fa fa-pencil"/>
            </CButton>
            <CButton
                size="sm"
                color="danger"
                @click="activeDelete = selectedAlumnis"
            >
              Delete selection
              <i class="fa fa-trash"/>
            </CButton>
          </div>
          <span v-else/>
        </div>
      </template>
      <template slot="actions" slot-scope="{ row }">
        <div class="d-flex gap-2 align-items-center">
          <CButton
            size="sm"
            color="primary"
            @click="openEditModal(row)"
          >
            <i class="fa fa-pencil"/>
          </CButton>
          <CButton
            size="sm"
            color="danger"
            @click="activeDelete = [row.form_uuid]"
          >
            <i class="fa fa-trash"/>
          </CButton>
        </div>
      </template>
      <template slot="form_url" slot-scope="{ row }">
        <CLink
          :href="row.form_url"
          target="_blank"
          data-cy="form-link"
        >
          lien unique
        </CLink>
      </template>
      <template slot="promotion" slot-scope="{ row }">
        {{ row.promotion }}
      </template>
      <template slot="response_status" slot-scope="{ row }">
        <span class="text-capitalize">
          {{ row.response_status.replace('_', ' ') }}
        </span>
      </template>
    </table-component>

    <CModal
      :show.sync="showSendEmailModal"
      :draggable="false"
      :resizable="false"
      centered
    >
      <template slot="header-wrapper"><span></span></template>
      Souhaitez-vous envoyer ce mail au(x) {{ getAlumniCount() }} alumni sélectionné(s)?
      <div
          v-if="shouldShowWarning"
          class="warning-alumni"
      >
        Attention : {{completedAlumni}} formulaire(s) sélectionné(s) sont déjà complété(s)!
      </div>
      <template slot="footer">
        <div>
          <CButton color="primary" class="mr-2" @click="showSendEmailModal=false">
            Annuler
          </CButton>
          <CButton color="primary" @click="sendEmails">
            Envoyer
          </CButton>
        </div>
      </template>
    </CModal>
    <CModal
      v-if="activeDelete"
      :show.sync="activeDelete.length"
      centered
      :draggable="false"
    >
      <template slot="header-wrapper">
        <div/>
      </template>
      Etes vous sûr de vouloir supprimer {{ selectedCount }} alumni?
      <template slot="footer">
        <CButton color="secondary" class="mr-2" @click="activeDelete = null">
          Non
        </CButton>
        <CButton
          color="danger"
          @click="deleteAlumni"
          data-cy="confirm-delete-button"
        >
          Oui
        </CButton>
      </template>
    </CModal>
    <CModal
      :show.sync="showEditModal"
      v-if="editAlumni"
      :draggable="false"
      :resizable="false"
      centered
    >
      <template slot="header">
        Modification d'une alumni
      </template>
      <div class="d-flex flex-column gap-2">
        <template v-for="input in Object.keys(editFields)">
          <MzSelect
            v-if="editFields[input].type === 'select'"
            :label="editFields[input].label"
            :options="availableOptions[input]"
            :value.sync="editFields[input].value"
            searchable
          />
          <template v-else>
            <label>
              {{editFields[input].label}}
            </label>
            <MzDatePicker
              :config="{format: 'YYYY-MM-DD'}"
              v-if="editFields[input].type === 'date'"
              v-model="editAlumni[input]"
            />
            <CInput
              v-else
              :type="editFields[input].type"
              v-model="editAlumni[input]"
            />
          </template>
        </template>
      </div>
      <template slot="footer">
        <div>
          <CButton
            color="secondary"
            class="mr-2"
            @click="editAlumni = null"
          >
            Annuler
          </CButton>
          <CButton
            color="primary"
            @click="saveAlumniEdit"
          >
            Sauvegarder
          </CButton>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import {CAMPAGNES_ENDPOINTS, CampaignService} from '@/services/campaign';
import {errorMessage, formTypeOptionsMap} from "@/constants/common";
import {downloadFile} from "@/utils/request";
import {ALUMNI_ENDPOINTS, AlumniService} from "@/services/alumni";
import {FILES_ENDPOINTS, FilesService} from "@/services/files";
import {BASE_URL} from "@/constants/config";
import {CampaignMixin} from "@/mixins/campaignMixin";
import Task from "@/services/task";
import {ActiveUser} from "@/services/user";
import {CertService} from "@/services/certification";
import {EcoleService} from "@/services/ecole";

export default {
  name: "Campaign",
  mixins: [CampaignMixin],
  data() {
    return {
      BASE_URL,
      CAMPAGNES_ENDPOINTS,
      ALUMNI_ENDPOINTS,
      uploadType: null,
      formTypeOptionsMap,
      isEditing: false,
      editAlumni: null,
      showEditModal: false,
      currentAlumniCopy: null,
      currentAlumniId: "",
      currentFilters: {},
      availableCodes: [],
      selectedAlumnis: [],
      availableOptions: {
        'ecole': [],
        'certification_code': [],
        'target_duree_post_diplome': [
          '3 mois',
          '6 mois',
          '15 mois',
          '2 ans',
          '3 ans',
          '4 ans',
        ],
      },
      editFields: {
        certification_code: {
          value: null,
          label: "Certification code",
          type: 'select',
        },
        ecole: {
          value: null,
          label: "Ecole",
          type: 'select',
        },
        promotion: {
          label: "Promotion",
          type: "number",
        },
        jury_date: {
          label: "Date de Jury",
          type: "date",
        },
        email: {
          label: "Email",
        },
        first_name: {
          label: "Prenom",
        },
        last_name: {
          label: "Nom",
        },
        id_alumni: {
          label: "ID alumni",
        },
        phone: {
          label: "Telephone",
        },
        program: {
          label: "Cursus",
        },
        access_cocktail: {
          label: "Voie access cocktail",
        },
        signataire: {
          label: "Signataire",
        },
        target_duree_post_diplome: {
          label: "Durée post diplôme cible",
          type: "select",
          value: null,
        },
        session: {
          label: "Session",
        },
        CKTL_STUDENT_ID: {
          label: "CKTL_STUDENT_ID",
        },
        CKTL_SAISON: {
          label: "CKTL_SAISON",
        },
        CKTL_RSY_ID: {
          label: "CKTL_RSY_ID",
        },
        CKTL_SCHOOL_ID: {
          label: "CKTL_SCHOOL_ID",
        },
        OPPY_ID: {
          label: "OPPY_ID"
        },
        CRM_CONVENTION_ID: {
          label: "CRM_CONVENTION_ID",
        },
        CRM_TITRE_ID: {
          label: "CRM_TITRE_ID",
        }
      },
      refreshAlumnis: 0,
      activeDelete: null,
      isAdministrator: ActiveUser.isAdministrator(),
      campaignCopy: {},
      tableColumns: [
        "form_uuid",
        "response_status",
        { name: "modification_date", type: "date" },
        { name: "email_date", type: "date" },
        "form_url",
        "email",
        "first_name",
        "last_name",
        "certification_code",
        "ecole",
        "promotion",
        { name: "jury_date", type: "date" },
        "target_duree_post_diplome",
      ],
      tableHeadings: {
        "response_status": "Statut réponse",
        "modification_date": "Dernière modification",
        "form_url": "Réponse formulaire URL",
        "first_name": "Prenom",
        "last_name": "Nom",
        "ecole": "Ecole (UC)",
        "jury_date": "Date de Jury",
        "email_date": "Dernier envoi mail",
        "target_duree_post_diplome": "Durée post diplôme cible",
        "actions": ""
      },
      showSendEmailModal: false,
      isUploading: false,
      shouldSelectAllRows: false,
      totalCount: 0,
      shouldShowWarning: false,
      completedAlumni: 0,
      filteredAlumnis: {}
    }
  },
  computed: {
    FILES_ENDPOINTS() {
      return FILES_ENDPOINTS
    },
    multiSelected() {
      return !!this.selectedAlumnis.length
    },
    selectedCount() {
      return this.shouldSelectAllRows ? this.totalCount : this.selectedAlumnis.length || 1;
    },
    computedColumns() {
      if (this.isAdministrator) {
        return [...this.tableColumns, "actions"];
      }
      return this.tableColumns
    }
  },
  async mounted() {
    await this.fetchCampaign();
    this.campaignCopy = {...this.campaign}
  },
  methods: {
    downloadFile,
    selectAlumnis(rows, shouldSelectAllRows) {
      this.shouldSelectAllRows = shouldSelectAllRows;
      this.selectedAlumnis = rows.map(row => row.form_uuid)
    },
    handleEdit() {
      if (this.isEditing) this.updateCampaign()
      this.isEditing = !this.isEditing
    },
    setFilters(filteredResults, __, totalCount) {
      this.totalCount = totalCount;
      this.currentFilters = Object.fromEntries(
        Object.entries(this.$refs.alumniTable.filterValues).filter(([f, value]) => {
          return !!value
        })
      )
      this.filteredAlumnis = filteredResults
    },
    getAlumniCount() {
      return this.$refs.alumniTable?.count;
    },
    async handleSend() {
      if (!this.getAlumniCount()) {
        return alert("No alumni found for given filters");
      }
      if (!this.campaign?.campaign_header) {
        return alert("Header campagne manquant");
      }
      await this.checkForCompletedAlumni()
      this.showSendEmailModal = true;
    },
    async sendEmails() {
      const { data } = await CampaignService.sendEmails(
        this.$route.params.campaign_id,
        this.$refs.alumniTable.tableQuery
      );
      this.taskId = data?.task_id;
      const sendMailTask = new Task(this.taskId)
      sendMailTask.on('result', (result) => {
        if (result.hasOwnProperty("missing_ecole_mails") && Array.isArray(result.missing_ecole_mails) && result.missing_ecole_mails.length !== 0) {
          alert(`WARNING : Aucune adresse mail n'est associée à : ${result.missing_ecole_mails.join(", ")}`)
        } else {
          alert("Successfully finished sending emails");
        }
      })
      sendMailTask.on('error', (error) => {
        alert(`Error: Something went wrong ${error}`)
      })

      alert("Successfully started sending emails");
      this.showSendEmailModal = false;
    },
    async exportScopeOfCampain() {
      const date = new Date()
      const dateStamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      const fileName = `Fichier_invitation_${this.campaign.name}_${dateStamp}.xlsx`
      await downloadFile({
        fileName: fileName,
        downloadURL: ALUMNI_ENDPOINTS.exportTemplate(this.campaign.id)
      })
    },
    async deleteAlumni() {
      const { res } = await AlumniService.deleteAlumni(
        this.$route.params.campaign_id,
        {
          all: this.shouldSelectAllRows,
          form_uuid: this.activeDelete,
          filters: this.$refs.alumniTable.tableQuery,
        }
      );
      if (res.status !== 200) {
        alert(errorMessage)
      }
      this.activeDelete = null;
      this.selectedAlumnis = [];
      await this.fetchCampaign();
      this.refreshAlumnis += 1;
    },
    async saveAlumniEdit() {
      const body = {
        form_uuid: this.currentAlumniId,
        all: this.shouldSelectAllRows,
        filters: this.$refs.alumniTable.tableQuery,
      };
      this.setFilters();
      Object.keys(this.editAlumni).forEach((field) => {
        if (this.editFields[field].type === 'select' && this.editFields[field].value !== null) {
          this.editAlumni[field] = this.editFields[field].value
          this.editFields[field].value = null;
        }
        if (this.editAlumni[field] !== this.currentAlumniCopy[field]) {
          body[field] = this.editAlumni[field]
        }
      });
      if (Object.keys(body).filter((v) =>  v !== 'form_uuid').length) {
        const {res} = await AlumniService.editAlumni(
          this.$route.params.campaign_id,
          body,
        );
        if (res.status !== 200) {
          alert("Something went wrong");
        }
      }
      this.editAlumni = null;
      this.showEditModal = false;
      this.currentAlumniCopy = null;
      this.currentAlumniId = "";
      await this.fetchCampaign();
      this.refreshAlumnis += 1;
    },
    async openEditModal(alumni, bulk=false) {
      this.currentAlumniId = bulk ? alumni : [alumni.form_uuid];
      this.editAlumni = {};
      for(const field in this.editFields) {
        let value = bulk ? "" : alumni[field]
        if (this.editFields[field].type === 'select') {
          this.editFields[field].value = bulk ? null : value;
        }
        this.editAlumni[field] = value
      }
      this.currentAlumniCopy = {...this.editAlumni}
      await this.fetchAvailableCerts();
      await this.fetchAvailableEcoles();
      this.showEditModal = true;
    },
    async fetchAvailableEcoles() {
      const {data} = await EcoleService.getAvailableEcoleNames();
      this.availableOptions['ecole'] = data
    },
    async fetchAvailableCerts() {
      const {data} = await CertService.getAvailableCodes();
      this.availableOptions['certification_code'] = data;
    },
    async uploadFile(files) {
      this.isUploading = true;
      this.$refs['upload'].closeModal()
      const url = this.uploadType === 'alumni' ?
          ALUMNI_ENDPOINTS.base + this.campaign.id + "/" : ALUMNI_ENDPOINTS.uploadInvitation(this.campaign.id)
      await FilesService.uploadFile({
        file: files[0],
        url: url,
        onError: (res) => {
          this.$refs['upload'].closeModal()
          alert(res.detail);
          this.isUploading = false;
        }
      });
      this.isUploading = false;
      this.$refs['upload'].closeModal()
      await this.fetchCampaign()
    },
    async updateCampaign() {
      const { res, data } = await CampaignService.updateCampaign(
          this.campaign.id, this.campaignCopy
      )
      if (res.status === 200) {
        this.campaign = {...data}
        this.saveCampaignToState()
      }
      else {
        alert(errorMessage)
        this.campaignCopy = {...this.campaign}
      }
    },
    async checkForCompletedAlumni(){
      this.completedAlumni = this.filteredAlumnis.filter((alumni) => {
        return (alumni.response_status === 'complété')
      }).length
      this.shouldShowWarning = !!this.completedAlumni;
    }
  }
}
</script>

<style lang="scss" scoped>
.buttons-wrapper {
  display: flex;
  gap: 0.5rem;

  & > div {
    flex: 1;
  }

  .btn {
    width: 100%;
  }
}
.warning-alumni{
  color: red;
}
</style>
